/**
 * useScrollAnimation
 * ------------------
 * Custom hook that uses IntersectionObserver to detect when an element
 * enters the viewport. Returns a [ref, isVisible] tuple.
 *
 * Once the element becomes visible it stays visible (one-shot trigger)
 * so animations only play once as the user scrolls down.
 *
 * Usage:
 *   const [ref, isVisible] = useScrollAnimation(0.15);
 *   <div ref={ref} className={isVisible ? styles.visible : styles.hidden}>
 */
import { useEffect, useRef, useState } from 'react';

const useScrollAnimation = (threshold = 0.12, rootMargin = '0px') => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Unobserve after first trigger — animation plays once
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin]);

  return [ref, isVisible];
};

export default useScrollAnimation;
